import { useMemo } from 'react';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { useEventStore } from '@/state/stores/use-event-store';
import { getFlowModerators } from '@/utils/contributor-extraction.utils';
import { useGetFlowQuery } from '@/state/queries/flows/use-get-flow';

const normalizeId = (value?: unknown) => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if ('id' in value && typeof (value as { id?: string }).id === 'string') {
      return (value as { id: string }).id;
    }
    if ('_id' in value && typeof (value as { _id?: string })._id === 'string') {
      return (value as { _id: string })._id;
    }
    if ('$oid' in value && typeof (value as { $oid?: string }).$oid === 'string') {
      return (value as { $oid: string }).$oid;
    }
  }
  return undefined;
};

/**
 * Flow moderators derived from event-store contributors, aligned with the latest GET /flows response.
 * After removing a moderator, GET_FLOW refetches (see useDeleteFlowContributor); intersecting with
 * flow.contributors drops stale rows immediately, similar to session actors + useGetSessionQuery.
 */
export function useFlowModeratorsSynced(flow: FlowDto, eventSlotId?: string | null) {
  const { contributors, event } = useEventStore();
  const flowOriginId = normalizeId(flow.origin) || flow.id;
  const { data: freshFlow } = useGetFlowQuery(flowOriginId, eventSlotId ?? undefined, {
    staleTime: 0,
    refetchOnMount: true,
  });
  const flowWithPlaylists = useMemo(() => {
    const baseFlow = freshFlow ?? flow;
    if (freshFlow?.contributorPlaylists) {
      return { ...baseFlow, contributorPlaylists: freshFlow.contributorPlaylists };
    }
    return baseFlow;
  }, [flow, freshFlow]);
  const flowEntityId = normalizeId(flowWithPlaylists.origin) || flowWithPlaylists.id;
  const flowForRoles = useMemo(
    () => ({
      ...flowWithPlaylists,
      id: flowEntityId,
      origin: flowEntityId,
    }),
    [flowWithPlaylists, flowEntityId],
  );

  const linkedContributorIds = useMemo(() => {
    const list = flowWithPlaylists.contributors;
    if (!Array.isArray(list)) return undefined;
    const ids = new Set<string>();
    for (const fc of list) {
      const c = fc.contributor;
      const id = typeof c === 'string' ? c : c?.id;
      if (id) ids.add(id);
    }
    return ids;
  }, [flowWithPlaylists.contributors]);

  const flowModerators = useMemo(() => {
    const all = getFlowModerators(flowForRoles, contributors || [], event?.multiFlow);
    if (!linkedContributorIds) return all;
    return all.filter((m) => {
      const isEventLevelModerator =
        event?.multiFlow === false && m.roles.some((r) => r.role === 'moderator' && r.entityType === 'Event');
      if (isEventLevelModerator) return true;
      return linkedContributorIds.has(m.id);
    });
  }, [flowForRoles, contributors, event?.multiFlow, linkedContributorIds]);

  return { flowForRoles, flowModerators, flowEntityId };
}
